import { isEqual } from 'lodash-es'
import { calcFullscreenSizeSub } from '../app-state/state.screen'
import { LayoutEvent } from '../constants/enums'
import { isAndroid, isIDevice, isIpad } from '../utilities/browser'
import { pubSubLayout } from './pubSub'

export type TLayoutState = {
    expandedNav: boolean
    expandedSearchMobile: boolean
    fullWindow: boolean
    minimized: boolean
}
export type TLayout = {
    layout: TLayoutState
    portal: TPortalState
    portalDimensions: TPortalDimensions
}

export type TPortalState = {
    composeFocused: boolean
    /** whether or not pinToTop or other movement should happen. if true we shouldn't move video */
    quietFocus: boolean
    division: number
}
export type TPortalDimensions = {
    height: number
    width: number
    listeners: number
}

export const LAYOUTSTATE: TLayout = {
    layout: {
        expandedNav: undefined,
        expandedSearchMobile: undefined,
        fullWindow: undefined,
        minimized: undefined,
    },
    portal: {
        composeFocused: undefined,
        quietFocus: undefined,
        division: undefined,
    },
    portalDimensions: {
        height: undefined,
        width: undefined,
        listeners: 0,
    },
}

if (typeof window !== 'undefined') {
    window.__MFC_APP_LAYOUT__ = LAYOUTSTATE
}

const setLayoutState = (layoutState: TLayoutState) => {
    if (isEqual(LAYOUTSTATE.layout, layoutState)) return
    LAYOUTSTATE.layout = layoutState
    pubSubLayout.emit(LayoutEvent.LAYOUT_CHANGE, layoutState, true)
}

export const updateLayoutState = (layoutState: Partial<TLayoutState>) => {
    if (!layoutState || typeof layoutState !== 'object') return
    const { expandedNav: prevExpandedNav } = LAYOUTSTATE.layout
    const nextLayoutState = { ...LAYOUTSTATE.layout, ...layoutState }
    // console.log('[updateLayoutState]', layoutState)
    setLayoutState(nextLayoutState)

    if (!!nextLayoutState.expandedNav !== !!prevExpandedNav) {
        resetPortalDimensions(true)
    }
    calcFullscreenSizeSub(nextLayoutState)
}

const setPortalState = (portalState: TPortalState, immediate = true) => {
    if (isEqual(LAYOUTSTATE.portal, portalState)) return
    LAYOUTSTATE.portal = portalState
    pubSubLayout.emit(LayoutEvent.PORTAL_CHANGE, portalState, immediate)
}

export const updatePortalState = (
    portalState: Partial<TPortalState>,
    immediate = true,
) => {
    if (!portalState || typeof portalState !== 'object') return
    const nextPortalState = { ...LAYOUTSTATE.portal, ...portalState }
    setPortalState(nextPortalState, immediate)
}

const setPortalDimensions = (dimensions: TPortalDimensions) => {
    if (isEqual(LAYOUTSTATE.portalDimensions, dimensions)) return
    LAYOUTSTATE.portalDimensions = dimensions
    pubSubLayout.emit(LayoutEvent.PORTAL_DIMENSIONS_CHANGE, dimensions, true)
}

export const resetPortalDimensions = (widthOnly?: boolean) => {
    const { height, width, listeners } = LAYOUTSTATE.portalDimensions
    if (listeners > 0) return
    if (height === undefined && width === undefined) return
    setPortalDimensions({
        ...LAYOUTSTATE.portalDimensions,
        ...(!widthOnly ? { height: undefined } : {}),
        width: undefined,
    })
}

export const updateFocus = (quietFocus = false) => {
    if (isIDevice || isIpad || isAndroid) {
        updatePortalState({ composeFocused: true, quietFocus })
    }
}

export const updateBlur = (quietFocus = false) => {
    if (isIDevice || isIpad || isAndroid) {
        updatePortalState({ composeFocused: false, quietFocus })
    }
}
export const onInputFocus = () => {
    if (isIDevice || isIpad || isAndroid) {
        updatePortalState({ composeFocused: true, quietFocus: false })
    }
}

export const onInputBlur = () => {
    if (isIDevice || isIpad || isAndroid) {
        updatePortalState({ composeFocused: false, quietFocus: false })
    }
}
